import { useEffect, useState } from "react";
import { Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

const CHOICES = [25, 89, 119, 139];
const sel = "h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2";

function FeeButtons({ value, onPick, testPrefix }) {
  return (
    <div className="flex gap-1">
      {CHOICES.map((f) => (
        <button key={f} data-testid={`${testPrefix}-${f}`} onClick={() => onPick(f)}
          className={`font-mono text-[11px] px-2 py-1 rounded border transition-colors ${value === f ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "border-slate-700 text-slate-400 hover:text-slate-100 hover:border-slate-500"}`}>
          ${f}
        </button>
      ))}
    </div>
  );
}

export function MerchantFeePanel({ merchant, onDone }) {
  const fees = merchant.fees || [];
  const latest = fees.length ? fees[fees.length - 1].fee : merchant.history?.[merchant.history.length - 1]?.fee_rate;
  const [fee, setFee] = useState(CHOICES.includes(latest) ? latest : null);
  const [from, setFrom] = useState(merchant.first_month);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFrom(merchant.first_month); setFee(CHOICES.includes(latest) ? latest : null); }, [merchant.merchant_id, merchant.first_month, latest]);
  if (merchant.is_house) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/merchants/${merchant.merchant_id}/fee`, { fee, from_month: from });
      toast.success(`Monthly fee set to $${fee} from ${monthLabel(from)}`); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (f) => {
    try { await api.delete(`/merchants/${merchant.merchant_id}/fee/${f.from_month}`); toast.success("Fee change removed"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Monthly merchant fee" action={<Receipt size={16} className="text-slate-400" />}>
      <div className="p-5 space-y-4" data-testid="merchant-fee-panel">
        {fees.length === 0
          ? <div className="text-xs text-amber-300" data-testid="merchant-fee-unset">No fee picked yet — using the agent's fee.</div>
          : <div className="space-y-1" data-testid="merchant-fee-history">{fees.map((f) => (
              <div key={f.from_month} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-emerald-400 w-16">{money(f.fee)}</span>
                <span className="text-slate-300">from {monthLabel(f.from_month)}</span>
                <span className="text-[11px] text-slate-500 flex-1">by {f.set_by}</span>
                <button data-testid={`remove-fee-${f.from_month}`} onClick={() => remove(f)} className="text-slate-600 hover:text-rose-400"><Trash2 size={13} /></button>
              </div>))}</div>}
        <div className="flex flex-wrap items-center gap-3">
          <FeeButtons value={fee} onPick={setFee} testPrefix="merchant-fee" />
          <select data-testid="merchant-fee-from" value={from} onChange={(e) => setFrom(e.target.value)} className={sel}>
            {(merchant.months || []).map((m) => <option key={m} value={m}>from {monthLabel(m)}</option>)}
          </select>
          <Button data-testid="merchant-fee-save" size="sm" disabled={busy || !fee} onClick={save} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Saving…" : "Save fee"}</Button>
        </div>
      </div>
    </Panel>
  );
}

export function PendingFees({ month, className = "" }) {
  const [d, setD] = useState(null);
  const [checked, setChecked] = useState([]);
  const load = () => api.get("/merchant-fees/pending", { params: month ? { month } : {} }).then((r) => setD(r.data))
    .catch((e) => { toast.error(errMsg(e)); setD({ merchants: [] }); });
  useEffect(() => { load(); setChecked([]); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async (ids, fee) => {
    try {
      const { data } = await api.post("/merchant-fees/bulk", { merchant_ids: ids, fee });
      toast.success(`$${fee} monthly fee set for ${data.updated} merchant${data.updated === 1 ? "" : "s"}`);
      setChecked([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const list = d?.merchants || [];
  return (
    <Panel title={`${month ? `New merchants in ${monthLabel(month)}` : "Merchants without a fee"} · pick monthly fee${d ? ` · ${list.length}` : ""}`} className={className}>
      {!d ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty>{month ? "No new merchants need a fee this month." : "Every merchant has a fee picked."}</Empty> : (
        <div data-testid="pending-fees-panel">
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#0d1320] border-b border-slate-800">
            <Checkbox data-testid="pending-fees-select-all" checked={checked.length === list.length}
              onCheckedChange={(v) => setChecked(v ? list.map((m) => m.merchant_id) : [])} />
            <span className="text-xs text-slate-400 flex-1">{checked.length ? `${checked.length} selected — set fee:` : "Select merchants to set many at once"}</span>
            {checked.length > 0 && <FeeButtons onPick={(f) => apply(checked, f)} testPrefix="pending-bulk-fee" />}
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-800">
            {list.map((m) => (
              <div key={m.merchant_id} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`pending-fee-${m.merchant_id}`}>
                <Checkbox checked={checked.includes(m.merchant_id)} data-testid={`pending-fee-check-${m.merchant_id}`}
                  onCheckedChange={(v) => setChecked((c) => (v ? [...c, m.merchant_id] : c.filter((x) => x !== m.merchant_id)))} />
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm text-slate-100">{m.merchant_name || "—"}</div>
                  <div className="text-[11px] font-mono text-slate-500">{m.merchant_id} · {m.owner} · since {monthLabel(m.first_month)} · now {money(m.current_fee)}</div>
                </div>
                <FeeButtons onPick={(f) => apply([m.merchant_id], f)} testPrefix={`pending-fee-${m.merchant_id}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
